import React, { useCallback, useEffect, useRef, useState, RefObject, cloneElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Text from 'components/Text'
import { BodyPortal } from 'components'
import TimeInput, { onTimeChangeParam } from 'components/DateTimePicker/TimeInput'
import { convertToContinentalTime } from 'components/DateTimePicker/helper'

import { KeyCode } from 'utils/constants/keyCode'

import { PopOverItem, PopOverProps } from './types'
import { Button as TargetButton, ButtonTextWrapper, CaretIcon, ChildWrapper, Container } from './styles'
import PopOverMenu from './PopOverMenu'
import PopOverLabel, { resolveTextCase } from './PopOverLabel'

export const SELECT_ALL_VALUE: PopOverItem<string> = { value: 'selectAll', label: 'select_all_btn', isRadio: true }

const getUmamiEventValue = (value?: PopOverItem | PopOverItem[]): string => {
  if (!value) return ''
  if (Array.isArray(value))
    return value
      .map((item) => item.label || item.value)
      .filter(Boolean)
      .join(', ')
  return value.label || value.value || ''
}

const PopOver = ({
  children,
  closeIcon,
  color = 'gray5',
  displayValue,
  highlighted = false,
  icon,
  iconColor,
  inline,
  isDisabled = false,
  isMultiple = false,
  isSearchable = false,
  isWithSelectAll,
  name,
  onBlur,
  onFocus,
  onToggleMenu,
  options: optionsList,
  openIcon,
  placeholder,
  showArrow = true,
  title,
  width = 'max-content',
  isPortaled = false,
  isSelectedOnTop = false,
  textColor,
  backgroundColor,
  isInputSearchHandle = false,
  timeInputRef,
  timeInputErrorRef,
  umamiEvent,
  ...props
}: PopOverProps) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState(optionsList)
  const [targetRef, setTargetRef] = useState<HTMLButtonElement | HTMLDivElement | null>(null)

  useEffect(() => {
    setOptions(optionsList)
  }, [optionsList])

  const escapeListener = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === KeyCode.escape) {
        if (onToggleMenu) onToggleMenu(false)
        setIsOpen(false)
      }
    },
    [onToggleMenu],
  )

  const handleOpen = async (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation()

    if (isDisabled) {
      return
    }

    if (onToggleMenu) {
      onToggleMenu(!isOpen)
    }

    setIsOpen(!isOpen)
  }

  const optionLength = useMemo(
    () =>
      options?.reduce((acc, option) => {
        if (option.submenu) {
          return acc + option.submenu.length + 1
        }
        if (option.options) {
          return acc + option.options.length
        }
        return acc + 1
      }, 0) ?? 0,
    [options],
  )

  const resolveDefaultTargetDisplay = (): React.ReactNode => {
    const defaultDisplay = placeholder || title || ` `

    if (displayValue) {
      return t(displayValue)
    }

    if (isMultiple && isWithSelectAll && (!props.value || (Array.isArray(props.value) && props.value.length === 0))) {
      return t('all_lbl')
    }

    if (!props.value) {
      return defaultDisplay
    }

    if (Array.isArray(props.value)) {
      if (props.value.length === 0) {
        return defaultDisplay
      }

      if (props.value.length === optionLength && !isSelectedOnTop) {
        return t('all_lbl')
      }

      return t('selected_count', { count: props.value.length })
    }

    if (!Array.isArray(props.value) && props.value?.selectedLabel) {
      return cloneElement(props.value.selectedLabel, { color: isOpen ? 'white' : 'gray5' })
    }

    if (!Array.isArray(props.value)) {
      return props.value.label ? t(props.value.label) : t(props.value.value)
    }

    return ` `
  }

  const displayColor = highlighted ? 'blue1' : isOpen ? 'white' : color
  const valueTextColor = !isOpen && textColor ? textColor : displayColor

  const renderValueDisplay = () => {
    const display = resolveDefaultTargetDisplay()

    if (React.isValidElement(display)) {
      return display
    }

    if (!Array.isArray(props.value) && props.value?.label) {
      const textCase = resolveTextCase(props.value, optionsList)

      if (textCase) {
        return <PopOverLabel label={props.value.label} textCase={textCase} color={valueTextColor} />
      }
    }

    return <Text color={valueTextColor}>{display}</Text>
  }

  const umamiAttrs = umamiEvent
    ? {
        'data-umami-event': umamiEvent,
        'data-umami-event-value':
          getUmamiEventValue(props.value) || (typeof displayValue === 'string' ? displayValue : ''),
      }
    : undefined

  const defaultTarget = (
    <TargetButton
      ref={setTargetRef}
      disabled={isDisabled}
      isOpen={isOpen}
      isHighlighted={highlighted}
      onClick={handleOpen}
      width={width}
      icon={icon}
      iconColor={iconColor ?? displayColor}
      bgColor={backgroundColor}
      {...umamiAttrs}
    >
      <ButtonTextWrapper>
        <Text color={displayColor}>{title ? `${t(title)}: ` : ''}</Text>
        {renderValueDisplay()}
        {showArrow && (
          <CaretIcon
            icon={isOpen ? openIcon || 'chevron-up' : closeIcon || 'chevron-down'}
            color={displayColor}
            fontSize={14}
          />
        )}
      </ButtonTextWrapper>
    </TargetButton>
  )

  const onTimeOptionSearch = ({ hours, minutes, meridiem, inputValue }: onTimeChangeParam) => {
    !inputValue
      ? setOptions(optionsList)
      : setOptions(
          optionsList?.filter(
            (item) =>
              item.label.startsWith(
                convertToContinentalTime({ hours, minutes, meridiem, inputValue }).modifiedHours.toString(),
              ) && item.label.endsWith(meridiem),
          ),
        )
  }

  const getTarget = () => {
    if (isInputSearchHandle) {
      return (
        <div ref={setTargetRef}>
          <TimeInput
            timeInputErrorRef={timeInputErrorRef as RefObject<{ value: boolean }>}
            timeInputRef={timeInputRef}
            onClick={handleOpen}
            timeValue={(props?.value as PopOverItem).label || ''}
            onChangeSearchTime={onTimeOptionSearch}
          />
        </div>
      )
    }

    return children
      ? React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return (
              <ChildWrapper ref={setTargetRef} key={index} onClick={handleOpen} {...umamiAttrs}>
                {child}
              </ChildWrapper>
            )
          }
        })
      : defaultTarget
  }

  useEffect(() => {
    let abort = false

    if (!abort) {
      document.addEventListener('keyup', escapeListener)
    }

    return () => {
      abort = true
      document.removeEventListener('keyup', escapeListener)
    }
  }, [escapeListener])

  useEffect(() => {
    if (!inputRef.current) {
      return
    }

    if (isOpen) {
      inputRef.current.focus()
    } else {
      inputRef.current.blur()
    }
  }, [isOpen])

  const popupComponent = (
    <PopOverMenu
      data-testid="popup-component"
      {...{
        ...props,
        isDisabled,
        isMultiple,
        isSearchable,
        isWithSelectAll,
        onToggleMenu,
        options: optionsList,
        width,
        isSelectedOnTop,
        umamiEvent,
      }}
      targetRef={targetRef}
      onClose={() => setIsOpen(false)}
    />
  )

  return (
    <Container data-testid="popover-container" inline={inline} ref={ref} isDisabled={isDisabled}>
      {getTarget()}
      <input hidden name={name} onBlur={onBlur} onFocus={onFocus} ref={inputRef} />
      {isOpen && <>{isPortaled ? <BodyPortal>{popupComponent}</BodyPortal> : popupComponent}</>}
    </Container>
  )
}

export { PopOverMenu }

export default PopOver
